import type { SidekickArtifactDownloadedEvent } from '../components/AISidekickPanel/SidekickPanelContext';
import {
  SIDEKICK_FEATURE_EVENT,
  type SidekickFeatureEvent,
} from '../featureEvents';
import {
  SIDEKICK_LIFECYCLE_EVENT,
  type SidekickLifecycleEvent,
} from '../lifecycle';

/** Who and where a Sidekick Heap event happened for, as the host knows it. */
export type SidekickHeapContext = {
  organizationId?: string | null;
  organizationName?: string | null;
  fleetId?: string | null;
  /** Whether Sidekick runs inside the CSP shell. */
  isEmbedded: boolean;
  /**
   * Where the host runs (web, PWA, native app), sent on session start.
   * Only the host can tell, so it is left out when the host does not say.
   */
  platform?: string;
};

type HeapProperties = Record<string, unknown>;

/**
 * Builds the Heap properties every Sidekick event carries.
 * @param conversationId - Conversation the event happened in, if any.
 * @param context - Organization and fleet Sidekick is showing.
 * @returns The shared Heap properties.
 */
const getSidekickBaseHeapProperties = (
  conversationId: string | null | undefined,
  { organizationId, organizationName, fleetId, isEmbedded }: SidekickHeapContext
) => ({
  orgId: organizationId || 'unknown',
  organizationName: organizationName || 'unknown',
  fleetId: fleetId || 'unknown',
  isEmbedded,
  conversationId: conversationId ?? 'unknown',
});

/**
 * Builds the Heap properties of an artifact download.
 * @param event - The download.
 * @param context - Organization and fleet Sidekick is showing.
 * @returns The Heap properties.
 */
export const getSidekickArtifactHeapProperties = (
  event: SidekickArtifactDownloadedEvent,
  { organizationId, fleetId, isEmbedded }: SidekickHeapContext
): HeapProperties => {
  const base = {
    orgId: organizationId || 'unknown',
    fleetId: fleetId || 'unknown',
    isEmbedded,
    artifactType: event.artifactType,
  };
  if (event.artifactType === 'chart') {
    return { ...base, position: event.position };
  }
  return { ...base, rowCount: event.rowCount, columnCount: event.columnCount };
};

/**
 * Builds the Heap properties of a lifecycle event.
 * @param event - The lifecycle event.
 * @param context - Organization and fleet Sidekick is showing.
 * @returns The Heap properties.
 */
export const getSidekickLifecycleHeapProperties = (
  event: SidekickLifecycleEvent,
  context: SidekickHeapContext
): HeapProperties => {
  const base = getSidekickBaseHeapProperties(event.conversationId, context);
  switch (event.type) {
    case SIDEKICK_LIFECYCLE_EVENT.SESSION_STARTED:
      return context.platform === undefined
        ? base
        : { ...base, platform: context.platform };
    case SIDEKICK_LIFECYCLE_EVENT.QUERY_SUBMITTED:
      return { ...base, inputLength: event.inputLength };
    case SIDEKICK_LIFECYCLE_EVENT.RESPONSE_RECEIVED:
      return {
        ...base,
        traceId: event.traceId ?? 'unknown',
        hasVisualization: event.hasVisualization,
        ...(event.latencyMs !== undefined
          ? { latencyMs: event.latencyMs }
          : {}),
      };
    case SIDEKICK_LIFECYCLE_EVENT.FEEDBACK_SUBMITTED:
      return {
        ...base,
        traceId: event.traceId ?? 'unknown',
        thumbsUp: event.thumbsUp,
      };
    case SIDEKICK_LIFECYCLE_EVENT.SESSION_ENDED:
      return {
        ...base,
        messageCount: event.messageCount,
        durationSeconds: event.durationSeconds,
      };
    default:
      return base;
  }
};

/**
 * Builds the Heap properties of a feature event. Answer-level actions
 * carry the answer's trace, with the same `'unknown'` fallback the
 * lifecycle events use.
 * @param event - The feature event.
 * @param context - Organization and fleet Sidekick is showing.
 * @returns The Heap properties.
 */
export const getSidekickFeatureHeapProperties = (
  event: SidekickFeatureEvent,
  context: SidekickHeapContext
): HeapProperties => {
  const base = getSidekickBaseHeapProperties(event.conversationId, context);
  switch (event.type) {
    case SIDEKICK_FEATURE_EVENT.MESSAGE_COPIED:
      return {
        ...base,
        target: event.target,
        ...(event.target === 'response'
          ? { traceId: event.traceId ?? 'unknown' }
          : {}),
      };
    case SIDEKICK_FEATURE_EVENT.RESPONSE_SHARED:
      return {
        ...base,
        traceId: event.traceId ?? 'unknown',
        shareType: event.shareType,
        recipientCount: event.recipientCount,
      };
    case SIDEKICK_FEATURE_EVENT.QUERY_EDITED:
      return { ...base, inputLength: event.inputLength };
    case SIDEKICK_FEATURE_EVENT.BRANCH_SWITCHED:
      return {
        ...base,
        direction: event.direction,
        branchCount: event.branchCount,
      };
    case SIDEKICK_FEATURE_EVENT.CONVERSATION_RESET:
      return { ...base, messageCount: event.messageCount };
    case SIDEKICK_FEATURE_EVENT.SUGGESTION_SELECTED:
      return {
        ...base,
        surface: event.surface,
        chipPosition: event.chipPosition,
        chipCount: event.chipCount,
      };
    case SIDEKICK_FEATURE_EVENT.UPSELL_VIEWED:
      return { ...base, hasExplorePlansCta: event.hasExplorePlansCta };
    default:
      return base;
  }
};
